import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, UserPlus } from "lucide-react";
import { toast } from "sonner";

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const token = params.get("token");
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/invite/${token}`);
        setInvite(data);
      } catch (e) {
        setError(apiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const accept = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setSubmitting(true);
    try {
      await api.post(`/invite/${token}/accept`, { password });
      await checkAuth();
      toast.success("Account created! Welcome aboard.");
      navigate("/my-tasks");
    } catch (err) { toast.error(apiError(err)); }
    finally { setSubmitting(false); }
  };

  const googleJoin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="grain flex min-h-screen items-center justify-center bg-background p-6">
      <div className="w-full max-w-md rounded-2xl border border-border bg-card p-8" data-testid="accept-invite-card">
        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : error ? (
          <div className="text-center">
            <h1 className="font-heading text-xl font-bold">Invitation unavailable</h1>
            <p className="mt-2 text-sm text-muted-foreground" data-testid="invite-error">{error}</p>
            <Button className="mt-4" onClick={() => navigate("/login")}>Go to login</Button>
          </div>
        ) : (
          <>
            <h1 className="font-heading text-2xl font-bold tracking-tight">Join {invite.business_name}</h1>
            <p className="mt-1 text-sm text-muted-foreground">Hi {invite.name}, set a password to activate your account for <span className="font-medium text-foreground">{invite.email}</span>.</p>
            <form onSubmit={accept} className="mt-6 space-y-4">
              <div className="space-y-1.5">
                <Label>Create a password</Label>
                <Input type="password" value={password} autoFocus data-testid="invite-password-input"
                  onChange={(e) => setPassword(e.target.value)} placeholder="At least 6 characters" />
              </div>
              <Button type="submit" disabled={submitting} data-testid="invite-accept-btn" className="w-full gap-2">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
                Accept &amp; create account
              </Button>
            </form>
            <div className="my-4 flex items-center gap-3 text-xs text-muted-foreground">
              <span className="h-px flex-1 bg-border" /> or <span className="h-px flex-1 bg-border" />
            </div>
            <Button type="button" variant="outline" onClick={googleJoin} data-testid="invite-google-btn" className="w-full gap-2">
              <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="h-4 w-4" />
              Continue with Google
            </Button>
            <p className="mt-2 text-center text-xs text-muted-foreground">Use the Google account for <span className="font-medium text-foreground">{invite.email}</span>.</p>
          </>
        )}
      </div>
    </div>
  );
}
